import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  // MdVerified,
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiUserLine,
  RiFileTextLine,
  RiCalendarLine,
  RiPhoneLine,
  RiInformationLine,
  RiBankCardLine,
  RiMailSendLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdVerified } from "react-icons/md";

const BASE_URL = `${process.env.REACT_APP_API_URL}/lic`;
const STAFF_URL = `${process.env.REACT_APP_API_URL}/admin`;

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n ?? 0);

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
        ${
          type === "success"
            ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
            : "bg-red-50 border border-red-200 text-red-800"
        }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, maxWidth = "max-w-lg" }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-indigo-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${maxWidth} mx-4 bg-white border border-indigo-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-indigo-100 bg-indigo-50">
          <h2 className="text-base font-semibold text-indigo-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-indigo-400 hover:text-indigo-700 transition rounded-lg p-1 hover:bg-indigo-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const StatCard = ({ label, value, icon: Icon, borderColor }) => (
  <div
    className={`flex items-center gap-4 bg-white border ${borderColor} rounded-xl px-5 py-4 shadow-sm`}
  >
    <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-md shadow-indigo-200">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">
        {label}
      </p>
      <p className="text-lg font-bold text-slate-800 leading-tight">{value}</p>
    </div>
  </div>
);

export default function StaffLic() {
  const [licRecords, setLicRecords] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const [createForm, setCreateForm] = useState({
    staffId: "",
    policyNo: "",
    premiumDueDate: "",
    dob: "",
    mobNo: "",
  });

  const [editForm, setEditForm] = useState({
    policyNo: "",
    premiumDueDate: "",
    dob: "",
    mobNo: "",
  });

  const [markPaidForm, setMarkPaidForm] = useState({
    paymentDate: "",
    remarks: "",
  });

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchLicRecords = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/get-all`);
      setLicRecords(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch LIC records", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${STAFF_URL}/all-users`);
      const all = res.data?.data || res.data || [];
      setStaffList(all.filter((u) => u.userType === "staff"));
    } catch {}
  };

  useEffect(() => {
    fetchLicRecords();
    fetchStaff();
  }, []);

  const summary = {
    total: licRecords.length,
    uniqueStaff: new Set(licRecords.map((x) => x.staffId?._id || x.staffId))
      .size,
    pendingDue: licRecords.filter((x) => {
      if (!x.premiumDueDate) return false;
      const dueDate = new Date(x.premiumDueDate);
      return dueDate <= new Date();
    }).length,
    upcomingDue: licRecords.filter((x) => {
      if (!x.premiumDueDate) return false;
      const dueDate = new Date(x.premiumDueDate);
      const today = new Date();
      const thirtyDaysFromNow = new Date(
        today.getTime() + 30 * 24 * 60 * 60 * 1000,
      );
      return dueDate > today && dueDate <= thirtyDaysFromNow;
    }).length,
    paidCount: licRecords.filter((x) => x.paymentCompleted).length,
    pendingPayment: licRecords.filter((x) => !x.paymentCompleted).length,
  };

  const handleCreate = async () => {
    if (
      !createForm.staffId ||
      !createForm.policyNo ||
      !createForm.premiumDueDate
    ) {
      return notify(
        "Staff, policy number, and premium due date are required",
        "error",
      );
    }

    if (createForm.policyNo.toString().trim() === "") {
      return notify("Policy number is required", "error");
    }

    setActionLoading(true);
    try {
      await axios.post(`${BASE_URL}/create`, {
        staffId: createForm.staffId,
        policyNo: parseInt(createForm.policyNo),
        premiumDueDate: createForm.premiumDueDate,
        dob: createForm.dob || null,
        mobNo: createForm.mobNo ? parseInt(createForm.mobNo) : null,
      });

      notify("LIC record created successfully");
      fetchLicRecords();
      setModal(null);
      setCreateForm({
        staffId: staffList[0]?._id || "",
        policyNo: "",
        premiumDueDate: "",
        dob: "",
        mobNo: "",
      });
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to create LIC record",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editForm.policyNo || !editForm.premiumDueDate) {
      return notify("Policy number and premium due date are required", "error");
    }

    if (editForm.policyNo.toString().trim() === "") {
      return notify("Policy number is required", "error");
    }

    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/update/${selected._id}`, {
        policyNo: parseInt(editForm.policyNo),
        premiumDueDate: editForm.premiumDueDate,
        dob: editForm.dob || null,
        mobNo: editForm.mobNo ? parseInt(editForm.mobNo) : null,
      });
      notify("LIC record updated successfully");
      fetchLicRecords();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to update LIC record",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/delete/${selected._id}`);
      notify("LIC record deleted successfully");
      fetchLicRecords();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to delete LIC record",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/mark-payment/${selected._id}`);
      notify("Payment marked as completed successfully");
      fetchLicRecords();
      setModal(null);
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to mark payment", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleManualReminder = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/check-reminders`);
      notify(response.data?.message || "Reminder check triggered successfully");
      fetchLicRecords();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to trigger reminders",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openCreate = () => {
    setCreateForm({
      staffId: staffList[0]?._id || "",
      policyNo: "",
      premiumDueDate: "",
      dob: "",
      mobNo: "",
    });
    setModal("create");
  };

  const openEdit = (record) => {
    setSelected(record);
    setEditForm({
      policyNo: record.policyNo,
      premiumDueDate: record.premiumDueDate
        ? new Date(record.premiumDueDate).toISOString().split("T")[0]
        : "",
      dob: record.dob ? new Date(record.dob).toISOString().split("T")[0] : "",
      mobNo: record.mobNo || "",
    });
    setModal("edit");
  };

  const openDelete = (record) => {
    setSelected(record);
    setModal("delete");
  };

  const openDetail = (record) => {
    setSelected(record);
    setModal("detail");
  };

  const openMarkAsPaid = (record) => {
    setSelected(record);
    setMarkPaidForm({
      paymentDate: new Date().toISOString().split("T")[0],
      remarks: "",
    });
    setModal("markPaid");
  };

  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const filtered = licRecords.filter((r) => {
    const name = r.staffId?.name || "";
    const email = r.staffId?.email || "";
    const policyNo = r.policyNo?.toString() || "";
    return (
      name.toLowerCase().includes(search.toLowerCase()) ||
      email.toLowerCase().includes(search.toLowerCase()) ||
      policyNo.toLowerCase().includes(search.toLowerCase())
    );
  });

  const getDueStatus = (dueDate) => {
    if (!dueDate) return { text: "No date", color: "slate" };
    const due = new Date(dueDate);
    const today = new Date();
    const daysLeft = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return { text: "Overdue", color: "red", days: Math.abs(daysLeft) };
    } else if (daysLeft <= 7) {
      return { text: "Due Soon", color: "amber", days: daysLeft };
    } else if (daysLeft <= 30) {
      return { text: "Upcoming", color: "blue", days: daysLeft };
    } else {
      return { text: "Not Due", color: "emerald", days: daysLeft };
    }
  };

  const getReminderStatus = (record) => {
    if (record.paymentCompleted)
      return { text: "Paid", color: "green", icon: "✅" };
    if (record.emailRemindersSent?.length > 0) {
      const lastReminder = Math.max(...record.emailRemindersSent);
      return {
        text: `Reminders Sent (${lastReminder}d)`,
        color: "orange",
        icon: "📧",
      };
    }
    return { text: "No Reminder", color: "gray", icon: "⏳" };
  };

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-indigo-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-md shadow-indigo-200">
              <MdVerified className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-indigo-800 tracking-tight">
                Staff LIC Insurance
              </h1>
              <p className="text-[11px] text-indigo-400 tracking-widest uppercase">
                Life Insurance Policy Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff or policy…"
                className="bg-indigo-50 border border-indigo-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 w-56 transition"
              />
            </div>

            <button
              onClick={fetchLicRecords}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-indigo-200 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>

            <button
              onClick={handleManualReminder}
              disabled={actionLoading}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-purple-200"
              title="Manually Check Deadlines & Send Reminders"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiMailSendLine className="w-4 h-4" />
              )}
              Check Reminders
            </button>

            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-indigo-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add LIC
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <StatCard
            label="Total Records"
            value={summary.total}
            icon={RiFileTextLine}
            borderColor="border-indigo-100"
          />
          <StatCard
            label="Unique Staff"
            value={summary.uniqueStaff}
            icon={RiUserLine}
            borderColor="border-purple-100"
          />
          <StatCard
            label="Overdue"
            value={summary.pendingDue}
            icon={RiErrorWarningLine}
            borderColor="border-red-100"
          />
          <StatCard
            label="Due within 30 days"
            value={summary.upcomingDue}
            icon={RiCalendarLine}
            borderColor="border-amber-100"
          />
          <StatCard
            label="Paid"
            value={summary.paidCount}
            icon={RiCheckLine}
            borderColor="border-green-100"
          />
          <StatCard
            label="Pending Payment"
            value={summary.pendingPayment}
            icon={RiErrorWarningLine}
            borderColor="border-orange-100"
          />
        </div>

        <div className="bg-white border border-indigo-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-indigo-50 bg-indigo-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
              <MdVerified className="w-4 h-4 text-indigo-500" />
              LIC Insurance Records
            </span>
            <span className="text-xs text-indigo-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-indigo-100 bg-indigo-50/30">
                  {[
                    "Staff",
                    "Policy Number",
                    "Mobile",
                    "DOB",
                    "Premium Due Date",
                    "Status",
                    "Payment",
                    "Actions",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 text-left">
                      <span className="text-xs font-semibold text-indigo-500 uppercase tracking-widest whitespace-nowrap">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-indigo-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-indigo-500" />
                        <span className="text-sm">Loading LIC records…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                          <MdVerified className="w-7 h-7 text-indigo-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search
                            ? "No records match your search."
                            : "No LIC insurance records yet."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((r, i) => {
                    const staff = r.staffId || {};
                    const dueStatus = getDueStatus(r.premiumDueDate);
                    const reminderStatus = getReminderStatus(r);

                    return (
                      <tr
                        key={r._id || i}
                        className="border-b border-indigo-50 hover:bg-indigo-50/50 transition group"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {staff.profilePic ? (
                              <img
                                src={staff.profilePic}
                                alt={staff.name}
                                className="w-8 h-8 rounded-full object-cover border border-indigo-100"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                {(staff.name || "?")[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-slate-800 whitespace-nowrap">
                                {staff.name || "—"}
                              </p>
                              <p className="text-[11px] text-slate-400">
                                {staff.position || "Staff"}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 border border-slate-200 text-slate-700 font-semibold text-xs">
                            <RiBankCardLine className="w-3.5 h-3.5" />
                            {r.policyNo || "—"}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600 flex items-center gap-1">
                            {r.mobNo ? (
                              <>
                                <RiPhoneLine className="w-3.5 h-3.5 text-indigo-400" />
                                {r.mobNo}
                              </>
                            ) : (
                              "—"
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-500">
                            {r.dob
                              ? new Date(r.dob).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "—"}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-600 whitespace-nowrap">
                            {r.premiumDueDate
                              ? new Date(r.premiumDueDate).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border
                            ${
                              dueStatus.color === "red"
                                ? "bg-red-50 text-red-700 border-red-200"
                                : dueStatus.color === "amber"
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : dueStatus.color === "blue"
                                    ? "bg-blue-50 text-blue-700 border-blue-200"
                                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                            }`}
                          >
                            <RiCalendarLine className="w-3 h-3" />
                            {dueStatus.text}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
                              ${
                                r.paymentCompleted
                                  ? "bg-green-100 text-green-700 border border-green-200"
                                  : "bg-yellow-50 text-yellow-700 border border-yellow-200"
                              }`}
                            >
                              {r.paymentCompleted ? "✅ Paid" : "⏳ Pending"}
                            </span>
                            {!r.paymentCompleted &&
                              reminderStatus.text !== "No Reminder" && (
                                <span className="text-[10px] text-orange-600">
                                  {reminderStatus.text}
                                </span>
                              )}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 flex-wrap">
                            <button
                              onClick={() => openDetail(r)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 font-semibold transition"
                              title="View Details"
                            >
                              <RiFileTextLine className="w-3.5 h-3.5" /> View
                            </button>

                            {!r.paymentCompleted && (
                              <button
                                onClick={() => openMarkAsPaid(r)}
                                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-green-50 border border-green-200 text-green-600 hover:bg-green-100 font-semibold transition"
                                title="Mark as Paid"
                              >
                                <RiCheckLine className="w-3.5 h-3.5" /> Mark
                                Paid
                              </button>
                            )}

                            <button
                              onClick={() => openEdit(r)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-600 hover:bg-indigo-100 font-semibold transition"
                              title="Edit Record"
                            >
                              <RiEditLine className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button
                              onClick={() => openDelete(r)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                              title="Delete Record"
                            >
                              <RiDeleteBinLine className="w-3.5 h-3.5" /> Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {modal === "create" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-indigo-500" /> Add LIC
              Insurance
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                Select Staff
              </label>
              <div className="relative">
                <RiUserLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300 pointer-events-none" />
                <select
                  value={createForm.staffId}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, staffId: e.target.value }))
                  }
                  className="w-full appearance-none bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-8 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                >
                  {staffList.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} — {s.position || "Staff"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                Policy Number
              </label>
              <div className="relative">
                <RiBankCardLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300" />
                <input
                  type="number"
                  value={createForm.policyNo}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      policyNo: e.target.value,
                    }))
                  }
                  placeholder="e.g. 1234567890"
                  className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                Premium Due Date
              </label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300 pointer-events-none" />
                <input
                  type="date"
                  value={createForm.premiumDueDate}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      premiumDueDate: e.target.value,
                    }))
                  }
                  className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                  Date of Birth
                </label>
                <div className="relative">
                  <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300 pointer-events-none" />
                  <input
                    type="date"
                    value={createForm.dob}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        dob: e.target.value,
                      }))
                    }
                    className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                  Mobile Number
                </label>
                <div className="relative">
                  <RiPhoneLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300" />
                  <input
                    type="tel"
                    value={createForm.mobNo}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        mobNo: e.target.value,
                      }))
                    }
                    placeholder="e.g. 9876543210"
                    className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                  />
                </div>
              </div>
            </div>

            <div className="mt-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
              <p className="text-xs text-indigo-700 flex items-start gap-1.5">
                <RiInformationLine className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Note:</strong> Premium due date helps track insurance
                  renewal deadlines. Mobile number and DOB are optional fields
                  for staff records.
                </span>
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition shadow-md shadow-indigo-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Create Record
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-indigo-500" /> Edit LIC —{" "}
              {selected?.staffId?.name}
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                Policy Number
              </label>
              <div className="relative">
                <RiBankCardLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300" />
                <input
                  type="number"
                  value={editForm.policyNo}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      policyNo: e.target.value,
                    }))
                  }
                  className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                Premium Due Date
              </label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300 pointer-events-none" />
                <input
                  type="date"
                  value={editForm.premiumDueDate}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      premiumDueDate: e.target.value,
                    }))
                  }
                  className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                  Date of Birth
                </label>
                <div className="relative">
                  <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300 pointer-events-none" />
                  <input
                    type="date"
                    value={editForm.dob}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        dob: e.target.value,
                      }))
                    }
                    className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                  Mobile Number
                </label>
                <div className="relative">
                  <RiPhoneLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300" />
                  <input
                    type="tel"
                    value={editForm.mobNo}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        mobNo: e.target.value,
                      }))
                    }
                    placeholder="e.g. 9876543210"
                    className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition shadow-md shadow-indigo-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {modal === "detail" && selected && (
        <Modal
          title={
            <>
              <RiFileTextLine className="w-5 h-5 text-indigo-500" /> LIC Detail
              — {selected.staffId?.name}
            </>
          }
          onClose={closeModal}
          maxWidth="max-w-md"
        >
          {(() => {
            const r = selected;
            const staff = r.staffId || {};
            const dueStatus = getDueStatus(r.premiumDueDate);

            return (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                  {staff.profilePic ? (
                    <img
                      src={staff.profilePic}
                      alt={staff.name}
                      className="w-10 h-10 rounded-full object-cover border border-indigo-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold">
                      {(staff.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-slate-800">
                      {staff.name || "—"}
                    </p>
                    <p className="text-xs text-slate-400">
                      {staff.email || ""}
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-bold text-indigo-900">
                    Policy Details
                  </h3>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-indigo-700 font-semibold">
                        Policy Number
                      </span>
                      <span className="text-base font-bold text-indigo-900">
                        {r.policyNo || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-indigo-700 font-semibold">
                        Premium Due Date
                      </span>
                      <span className="text-sm font-bold text-slate-800">
                        {r.premiumDueDate
                          ? new Date(r.premiumDueDate).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )
                          : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-indigo-700 font-semibold">
                        Status
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          dueStatus.color === "red"
                            ? "text-red-700"
                            : dueStatus.color === "amber"
                              ? "text-amber-700"
                              : dueStatus.color === "blue"
                                ? "text-blue-700"
                                : "text-emerald-700"
                        }`}
                      >
                        {dueStatus.text}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reminder History Section */}
                {r.emailRemindersSent && r.emailRemindersSent.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-orange-800 mb-2 flex items-center gap-1">
                      📧 Reminder History
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {r.emailRemindersSent
                        .sort((a, b) => a - b)
                        .map((day) => (
                          <span
                            key={day}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-orange-100 text-orange-700"
                          >
                            {day === 0 ? "Due Date" : `${day} days before`}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {/* Payment Status Section */}
                {r.paymentCompleted && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-green-800 flex items-center gap-1">
                      ✅ Payment Completed
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      No further reminders will be sent for this policy.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3">
                  {r.mobNo && (
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                        Mobile Number
                      </p>
                      <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <RiPhoneLine className="w-4 h-4 text-indigo-500" />
                        {r.mobNo}
                      </p>
                    </div>
                  )}

                  {r.dob && (
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                        Date of Birth
                      </p>
                      <p className="text-sm font-semibold text-slate-800">
                        {new Date(r.dob).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  )}

                  <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                      Record Created
                    </p>
                    <p className="text-sm font-bold text-slate-800">
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })
                        : "—"}
                    </p>
                  </div>

                  {r.updatedAt && r.updatedAt !== r.createdAt && (
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                        Last Updated
                      </p>
                      <p className="text-sm font-bold text-slate-800">
                        {new Date(r.updatedAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-2 border-t border-indigo-50">
                  <button
                    onClick={closeModal}
                    className="px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Mark as Paid Modal */}
      {modal === "markPaid" && selected && (
        <Modal
          title={
            <>
              <RiCheckLine className="w-5 h-5 text-green-500" /> Mark Payment
              Completed
            </>
          }
          onClose={closeModal}
        >
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800 font-medium mb-2">
                Confirm payment for:
              </p>
              <p className="font-bold text-slate-800">
                {selected.staffId?.name}
              </p>
              <p className="text-sm text-slate-600">
                Policy: {selected.policyNo}
              </p>
              <p className="text-sm text-slate-600">
                Due Date:{" "}
                {new Date(selected.premiumDueDate).toLocaleDateString("en-IN")}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                Payment Date
              </label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300 pointer-events-none" />
                <input
                  type="date"
                  value={markPaidForm.paymentDate}
                  onChange={(e) =>
                    setMarkPaidForm((f) => ({
                      ...f,
                      paymentDate: e.target.value,
                    }))
                  }
                  className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-indigo-700 uppercase tracking-widest">
                Remarks (Optional)
              </label>
              <textarea
                value={markPaidForm.remarks}
                onChange={(e) =>
                  setMarkPaidForm((f) => ({
                    ...f,
                    remarks: e.target.value,
                  }))
                }
                rows="2"
                placeholder="Payment mode, reference number, etc."
                className="w-full bg-indigo-50/60 border border-indigo-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800 flex items-start gap-1.5">
                <RiInformationLine className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Note:</strong> Marking payment as completed will stop
                  all future reminder emails for this policy.
                </span>
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleMarkAsPaid}
                disabled={actionLoading}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-green-600 hover:bg-green-700 text-white transition shadow-md shadow-green-200 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiCheckLine className="w-4 h-4" />
                )}{" "}
                Confirm Payment
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete LIC record for{" "}
              <span className="text-red-500">{selected?.staffId?.name}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. The insurance record will be
              permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}{" "}
                Delete Record
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
